import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronDown, ChevronRight, Handshake, Pencil, Phone } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LEAD_PRIORITIES, LEAD_STATUSES, LEAD_STATUS_BADGE, PRIORITY_BADGE, TIER_BADGE } from "@/lib/constants";
import { formatINR, formatSqft, sizeOf, yesNo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const Item = ({ label, value }) => (
  <div>
    <dt className="text-xs uppercase tracking-widest text-slate-400">{label}</dt>
    <dd className="mt-0.5 text-slate-800">{value == null || value === "" ? "—" : value}</dd>
  </div>
);

const Card = ({ title, children, testId }) => (
  <section className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-6 sm:p-8" data-testid={testId}>
    <h2 className="font-heading text-lg font-semibold text-emerald-950 mb-5 pb-3 border-b border-slate-100">{title}</h2>
    {children}
  </section>
);

const range = (lo, hi, fmt = (x) => x) =>
  lo == null && hi == null ? null : `${lo != null ? fmt(lo) : "Any"} – ${hi != null ? fmt(hi) : "Any"}`;

function TrackingCard({ lead, leadId, queryClient }) {
  const [status, setStatus] = useState(lead.status);
  const [priority, setPriority] = useState(lead.priority);
  const [followUp, setFollowUp] = useState(lead.next_follow_up_date || "");

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch(`/leads/${leadId}/tracking`, {
        status, priority, next_follow_up_date: followUp || null,
      })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Tracking updated");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  return (
    <Card title="Tracking" testId="lead-tracking-card">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        <div className="space-y-1.5">
          <Label className="text-slate-700">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="tracking-status" className="rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-700">Priority</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="tracking-priority" className="rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>{LEAD_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-700">Next Follow-up</Label>
          <Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="tracking-followup" className="rounded-lg" />
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="tracking-save" className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white">
          {save.isPending ? "Saving…" : "Save Tracking"}
        </Button>
      </div>
    </Card>
  );
}

function NotesCard({ lead, leadId, queryClient }) {
  const [text, setText] = useState("");
  const addNote = useMutation({
    mutationFn: async () => (await api.post(`/leads/${leadId}/notes`, { text })).data,
    onSuccess: () => {
      setText("");
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      toast.success("Note added");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });
  const notes = [...(lead.activity_notes || [])].reverse();

  return (
    <Card title="Activity Notes" testId="lead-notes-card">
      <div className="flex gap-3 mb-5">
        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder='e.g. "Called customer, wants a site visit this weekend"'
          data-testid="note-input"
          className="rounded-lg"
        />
        <Button onClick={() => addNote.mutate()} disabled={!text.trim() || addNote.isPending} data-testid="note-submit" className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white self-end">
          Add Note
        </Button>
      </div>
      {notes.length === 0 ? (
        <p className="text-slate-400 text-sm" data-testid="notes-empty">No activity notes yet.</p>
      ) : (
        <ul className="space-y-3" data-testid="notes-list">
          {notes.map((n, i) => (
            <li key={i} className="border-l-2 border-emerald-200 pl-4">
              <p className="text-slate-800">{n.text}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                {n.by_name || "Staff"} · {new Date(n.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MatchRow({ item, onCreateDeal, creating }) {
  const navigate = useNavigate();
  const p = item.property;
  return (
    <div className="flex flex-wrap items-center gap-4 py-4 border-b border-slate-100 last:border-0" data-testid={`match-row-${p.id}`}>
      <Badge variant="outline" className={`${TIER_BADGE[item.tier]} w-24 justify-center`} data-testid={`match-tier-${p.id}`}>{item.tier}</Badge>
      <div className="flex-1 min-w-[200px]">
        <button onClick={() => navigate(`/properties/${p.id}`)} className="font-medium text-emerald-950 hover:underline text-left">{p.title}</button>
        <p className="text-sm text-slate-500">{p.locality}, {p.city} · {sizeOf(p)}</p>
      </div>
      <span className="font-medium text-emerald-900 whitespace-nowrap">
        {formatINR(p.price_or_rent_amount)}{p.listing_type === "Rent" ? "/mo" : ""}
      </span>
      <Button size="sm" onClick={() => onCreateDeal(p.id)} disabled={creating} data-testid={`create-deal-${p.id}`} className="rounded-lg bg-emerald-900 hover:bg-emerald-950 text-white">
        <Handshake className="h-4 w-4 mr-1.5" /> Create Deal
      </Button>
    </div>
  );
}

function MatchesTab({ leadId }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showStretch, setShowStretch] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["lead-matches", leadId],
    queryFn: async () => (await api.get(`/leads/${leadId}/matches`)).data,
  });

  const createDeal = useMutation({
    mutationFn: async (propertyId) => (await api.post("/deals", { lead_id: leadId, property_id: propertyId })).data,
    onSuccess: (deal) => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      toast.success("Deal created — lead marked as Matched");
      navigate(`/deals/${deal.id}`);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  if (isLoading) return <p className="text-slate-400 p-6">Finding matches…</p>;
  const { matches = [], stretch_matches = [] } = data || {};

  return (
    <div className="space-y-6">
      <Card title={`Matching Properties (${matches.length})`} testId="matches-section">
        {matches.length === 0 ? (
          <p className="text-slate-400 text-sm" data-testid="matches-empty">
            No available properties pass the hard filters (type, Sale/Rent match, Available status) within budget.
          </p>
        ) : (
          matches.map((m) => (
            <MatchRow key={m.property.id} item={m} onCreateDeal={(pid) => createDeal.mutate(pid)} creating={createDeal.isPending} />
          ))
        )}
      </Card>

      {stretch_matches.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(6,78,59,0.08)] p-6 sm:p-8" data-testid="stretch-section">
          <button
            onClick={() => setShowStretch((s) => !s)}
            className="flex items-center gap-2 font-heading text-lg font-semibold text-amber-800 w-full"
            data-testid="stretch-toggle"
          >
            {showStretch ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            Stretch matches — within 10% of budget ({stretch_matches.length})
          </button>
          {showStretch && (
            <div className="mt-4 pt-2 border-t border-slate-100">
              {stretch_matches.map((m) => (
                <MatchRow key={m.property.id} item={m} onCreateDeal={(pid) => createDeal.mutate(pid)} creating={createDeal.isPending} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default function LeadDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";

  const { data: l, isLoading } = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
  });

  if (isLoading || !l) return <div className="p-8 text-slate-400">Loading…</div>;

  return (
    <div className="p-6 sm:p-8 max-w-5xl" data-testid="lead-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <button onClick={() => navigate("/leads")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-900 mb-3 transition-colors" data-testid="back-to-leads">
            <ArrowLeft className="h-4 w-4" /> All Leads
          </button>
          <h1 className="font-heading text-3xl font-bold text-emerald-950 tracking-tight" data-testid="lead-customer-name">{l.customer_name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Badge variant="outline" className={PRIORITY_BADGE[l.priority]} data-testid="lead-priority-badge">{l.priority}</Badge>
            <Badge variant="outline" className={LEAD_STATUS_BADGE[l.status]} data-testid="lead-status-badge">{l.status}</Badge>
            <Badge variant="outline" className="border-slate-200 text-slate-600">{l.enquiry_for} · {l.property_type_interested}{l.property_subtype_interested ? ` · ${l.property_subtype_interested}` : ""}</Badge>
            <span className="text-sm text-slate-500">
              Assigned to: <span className="font-medium text-slate-700" data-testid="lead-assigned-name">{l.assigned_to_name || "Unassigned"}</span>
            </span>
          </div>
        </div>
        {isAdmin && (
          <Button variant="outline" onClick={() => navigate(`/leads/${id}/edit`)} data-testid="edit-lead-button" className="rounded-lg">
            <Pencil className="h-4 w-4 mr-2" /> Edit Lead
          </Button>
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-lg mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-md">Overview</TabsTrigger>
          <TabsTrigger value="matches" data-testid="tab-matches" className="rounded-md">Matching Properties</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 animate-fade-up">
          <TrackingCard key={`${l.status}-${l.priority}-${l.next_follow_up_date}`} lead={l} leadId={id} queryClient={queryClient} />

          <Card title="Client Info">
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              <Item label="Phone (Primary)" value={<a href={`tel:${l.customer_phone_primary}`} className="text-emerald-800 font-medium inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{l.customer_phone_primary}</a>} />
              <Item label="Phone (Secondary)" value={l.customer_phone_secondary} />
              <Item label="Email" value={l.customer_email} />
              <Item label="Date of Birth" value={l.customer_dob} />
              <Item label="Occupation" value={l.customer_occupation} />
              <Item label="Address" value={l.customer_address} />
            </dl>
          </Card>

          <Card title="Requirement">
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              <Item label="Enquiry For" value={l.enquiry_for} />
              <Item label="Property Type" value={l.property_type_interested} />
              <Item label="Subtype" value={l.property_subtype_interested} />
              <Item label="Preferred Areas" value={l.preferred_areas} />
              <Item label="Preferred Streets" value={l.preferred_streets} />
              <Item label="City" value={l.city} />
              <Item label="Budget" value={range(l.budget_min, l.budget_max, formatINR)} />
              <Item label="Budget per sqft" value={range(l.budget_per_sqft_min, l.budget_per_sqft_max, formatINR)} />
              <Item label="Saleable Area" value={range(l.saleable_area_min, l.saleable_area_max, formatSqft)} />
              <Item label="Carpet Area" value={range(l.carpet_area_min, l.carpet_area_max, formatSqft)} />
              <Item label="Plot Area" value={range(l.plot_area_min, l.plot_area_max, formatSqft)} />
            </dl>
          </Card>

          <Card title="Preferences & Additional Info">
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              <Item label="Condition" value={l.condition_preference} />
              <Item label="BHK Requirement" value={l.bhk_requirement} />
              <Item label="Furnishing" value={l.furnishing_preference} />
              <Item label="Floor Preference" value={range(l.floor_preference_min, l.floor_preference_max)} />
              <Item label="Parking Required" value={yesNo(l.parking_required)} />
              <Item label="Loan Assistance" value={yesNo(l.loan_assistance_needed)} />
              <Item label="Pre-leased OK" value={yesNo(l.pre_leased_acceptable)} />
              <Item label="Facing Preference" value={l.facing_direction_preference} />
              <Item label="Amenities Required" value={l.amenities_required?.length ? l.amenities_required.join(", ") : null} />
              <Item label="Reference Source" value={l.reference_source} />
              <Item label="Declaration Signed" value={yesNo(l.declaration_signed)} />
              <Item label="Entry Date" value={l.entry_date} />
              <Item label="Comments" value={l.comments} />
            </dl>
          </Card>

          <NotesCard lead={l} leadId={id} queryClient={queryClient} />
        </TabsContent>

        <TabsContent value="matches" className="animate-fade-up">
          <MatchesTab leadId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
